import { useState, useEffect } from 'react';
import { Inbox, Loader2, Mail, Send, X, Edit3, ChevronDown, ChevronRight, RefreshCw, CheckCircle, AlertTriangle, Clock, UserX, MessageSquare } from 'lucide-react';
import { listReplies, updateReplyDraft, sendReplyEmail, dismissReply } from '../api/replies';

const CLASS_CONFIG = {
  interested:     { label: 'Interested',      color: 'bg-green-100 text-green-700', icon: CheckCircle },
  soft_interest:  { label: 'Soft Interest',   color: 'bg-emerald-100 text-emerald-700', icon: MessageSquare },
  objection:      { label: 'Objection',       color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  unsubscribe:    { label: 'Unsubscribe',     color: 'bg-red-100 text-red-700', icon: UserX },
  ooo:            { label: 'Out of Office',   color: 'bg-blue-100 text-blue-700', icon: Clock },
  not_interested: { label: 'Not Interested',  color: 'bg-slate-100 text-slate-600', icon: X },
  wrong_person:   { label: 'Wrong Person',    color: 'bg-purple-100 text-purple-700', icon: UserX },
  unknown:        { label: 'Unclassified',    color: 'bg-slate-100 text-slate-500', icon: Mail },
};

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'responded', label: 'Responded' },
  { key: 'dismissed', label: 'Dismissed' },
];

export default function Inboxes() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [sending, setSending] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchReplies = async () => {
    try {
      const res = await listReplies(filter);
      setReplies(res.replies || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchReplies();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendReply = async (replyId) => {
    setSending(replyId);
    setMsg(null);
    try {
      // If draft was edited, save first
      if (editDraft && editDraft.replyId === replyId) {
        await updateReplyDraft(replyId, {
          suggestedReply: editDraft.text,
          suggestedSubject: editDraft.subject,
        });
      }
      await sendReplyEmail(replyId);
      setMsg({ type: 'success', text: 'Reply sent successfully!' });
      setEditDraft(null);
      fetchReplies();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSending(null);
    }
  };

  const handleDismiss = async (replyId) => {
    try {
      await dismissReply(replyId);
      setReplies(prev => prev.map(r => r.replyId === replyId ? { ...r, status: 'dismissed' } : r));
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const toggleExpand = (replyId) => {
    setExpanded(expanded === replyId ? null : replyId);
    setEditDraft(null);
  };

  const hotCount = replies.filter(r => ['interested', 'soft_interest'].includes(r.classification) && r.status === 'new').length;
  const newCount = replies.filter(r => r.status === 'new').length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">
            {newCount > 0 ? `${newCount} new ${newCount === 1 ? 'reply' : 'replies'}` : 'No new replies'}
            {hotCount > 0 && <span className="ml-2 text-green-600 font-medium">{hotCount} hot lead{hotCount > 1 ? 's' : ''}</span>}
          </p>
        </div>
        <button onClick={() => { setLoading(true); fetchReplies(); }}
          className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {STATUS_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
          msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {msg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : replies.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900">No replies yet</h3>
          <p className="text-sm text-slate-500 mt-1">When prospects reply to your campaign emails, they'll appear here with AI-generated response drafts.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {replies.map(reply => {
            const cls = CLASS_CONFIG[reply.classification] || CLASS_CONFIG.unknown;
            const ClsIcon = cls.icon;
            const isExpanded = expanded === reply.replyId;
            const isHot = ['interested', 'soft_interest'].includes(reply.classification);

            return (
              <div key={reply.replyId}
                className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                  isHot && reply.status === 'new' ? 'border-green-300 shadow-sm shadow-green-100' : 'border-slate-200'
                }`}>
                {/* Row header */}
                <button onClick={() => toggleExpand(reply.replyId)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{reply.prospectName}</span>
                      <span className="text-xs text-slate-400 truncate">&lt;{reply.prospectEmail}&gt;</span>
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5">{reply.subject}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${cls.color}`}>
                      <ClsIcon className="w-3 h-3" />
                      {cls.label}
                    </span>
                    {reply.status === 'new' && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                    <span className="text-xs text-slate-400">{new Date(reply.createdAt).toLocaleDateString()}</span>
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-100 px-5 py-4 space-y-4">
                    {/* Original reply */}
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Their Reply</h4>
                      <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">
                        {reply.rawText}
                      </div>
                    </div>

                    {/* Confidence */}
                    {reply.confidence > 0 && (
                      <p className="text-xs text-slate-400">
                        Classification confidence: {Math.round(reply.confidence * 100)}%
                        {reply.extractedInfo?.specific_objection && ` — Objection: ${reply.extractedInfo.specific_objection}`}
                        {reply.extractedInfo?.return_date && ` — Returns: ${reply.extractedInfo.return_date}`}
                        {reply.extractedInfo?.referred_person && ` — Referred: ${reply.extractedInfo.referred_person}`}
                      </p>
                    )}

                    {/* Draft reply */}
                    {reply.suggestedReply && reply.status !== 'responded' && reply.status !== 'dismissed' && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">AI Draft Reply</h4>
                          {!editDraft?.replyId || editDraft.replyId !== reply.replyId ? (
                            <button onClick={() => setEditDraft({ replyId: reply.replyId, subject: reply.suggestedSubject || '', text: reply.suggestedReply })}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                              <Edit3 className="w-3 h-3" /> Edit
                            </button>
                          ) : null}
                        </div>

                        {editDraft?.replyId === reply.replyId ? (
                          <div className="space-y-2">
                            <input type="text" value={editDraft.subject}
                              onChange={(e) => setEditDraft({ ...editDraft, subject: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Subject" />
                            <textarea value={editDraft.text}
                              onChange={(e) => setEditDraft({ ...editDraft, text: e.target.value })}
                              rows={6}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                          </div>
                        ) : (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 space-y-1">
                            <p className="text-xs font-medium text-blue-700">Subject: {reply.suggestedSubject}</p>
                            <p className="text-sm text-blue-900 whitespace-pre-wrap">{reply.suggestedReply}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sent reply record */}
                    {reply.status === 'responded' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                          <CheckCircle className="w-4 h-4" />
                          <span>Reply sent to {reply.sentTo || reply.prospectEmail}</span>
                          {reply.respondedAt && <span className="text-xs text-green-500 ml-auto">{new Date(reply.respondedAt).toLocaleString()}</span>}
                        </div>
                        {(reply.sentBody || reply.suggestedReply) && (
                          <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 space-y-1">
                            <p className="text-xs font-medium text-green-700">Subject: {reply.sentSubject || reply.suggestedSubject || `Re: ${reply.subject}`}</p>
                            <p className="text-sm text-green-900 whitespace-pre-wrap">{reply.sentBody || reply.suggestedReply}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {reply.status === 'dismissed' && (
                      <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-3 py-2 rounded-lg">
                        <X className="w-4 h-4" /> Dismissed
                      </div>
                    )}

                    {/* Actions */}
                    {reply.status !== 'responded' && reply.status !== 'dismissed' && (
                      <div className="flex gap-2 pt-2">
                        {reply.suggestedReply && (
                          <button onClick={() => handleSendReply(reply.replyId)}
                            disabled={sending === reply.replyId}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                            {sending === reply.replyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Send Reply
                          </button>
                        )}
                        <button onClick={() => handleDismiss(reply.replyId)}
                          className="px-4 py-2 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 flex items-center gap-2">
                          <X className="w-4 h-4" /> Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
